import { useMemo } from 'react'
import CodeMirror, { type BasicSetupOptions } from '@uiw/react-codemirror'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter } from '@codemirror/lint'
import type { Extension } from '@codemirror/state'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'

export function formatJsonStringForEditor(value?: string | null) {
  if (!value?.trim()) return ''

  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

export function parseJsonString(value: string) {
  try {
    return { valid: true as const, parsed: JSON.parse(value) }
  } catch (error) {
    const message = error instanceof SyntaxError ? error.message : 'Must be valid JSON'
    return { valid: false as const, message }
  }
}

export function stringifyJsonForApi(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return null

  const result = parseJsonString(trimmed)
  if (!result.valid) {
    throw new Error(result.message)
  }

  return JSON.stringify(result.parsed)
}

const defaultJsonExtensions = [json(), linter(jsonParseLinter())]

type JsonInputProps = {
  value?: string | null
  onChange: (value: string) => void
  onBlur?: () => void
  height?: string
  className?: string
  editorClassName?: string
  disabled?: boolean
  readOnly?: boolean
  extensions?: Extension[]
  basicSetup?: boolean | BasicSetupOptions
  theme?: 'light' | 'dark'
}

export const JsonInput = ({
  value,
  onChange,
  onBlur,
  height = '240px',
  className,
  editorClassName,
  disabled = false,
  readOnly = false,
  extensions,
  basicSetup,
  theme = 'light',
}: JsonInputProps) => {
  const memoizedExtensions = useMemo(() => extensions ?? defaultJsonExtensions, [extensions])
  const memoizedBasicSetup = useMemo(
    () =>
      basicSetup ?? {
        lineNumbers: true,
        highlightActiveLine: false,
        highlightActiveLineGutter: false,
      },
    [basicSetup]
  )

  return (
    <div
      className={cn(
        'bg-offwhite focus-within:ring-offset-offwhite rounded-md border border-input text-sm shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        disabled && 'pointer-events-none opacity-60',
        className
      )}
    >
      <CodeMirror
        value={value ?? ''}
        height={height}
        theme={theme}
        extensions={memoizedExtensions}
        basicSetup={memoizedBasicSetup}
        onChange={(nextValue) => onChange(nextValue)}
        onBlur={onBlur}
        editable={!readOnly && !disabled}
        readOnly={readOnly || disabled}
        className={cn(
          '[&_div.cm-editor]:rounded-md [&_div.cm-editor]:bg-transparent [&_div.cm-editor]:text-sm',
          editorClassName
        )}
      />
    </div>
  )
}

type JsonInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>
  name: TName
  label: string
  description?: string
  className?: string
} & Omit<JsonInputProps, 'value' | 'onChange' | 'onBlur'>

export const JsonInputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  className,
  ...jsonInputProps
}: JsonInputFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormControl>
            <JsonInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} {...jsonInputProps} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
